import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { getToken, login } from '@/auth/keycloak'
import { currentLocale } from '@/i18n'

/** The error shape the service returns for everything. */
export interface ApiErrorPayload {
  timestamp?: string
  status?: number
  error?: string
  code?: string
  message?: string
  path?: string
  details?: Array<{ field?: string; message?: string }>
}

/**
 * An API failure, normalised. Carries the service's machine-readable code so a
 * screen can react to the specific failure - "not enough points" is handled
 * differently from "over the tier ceiling" - rather than matching on prose.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Array<{ field?: string; message?: string }>

  constructor(status: number, code: string, message: string, details: ApiErrorPayload['details'] = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details ?? []
  }

  /** Field errors keyed by field name, for marking up a form. */
  fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {}
    for (const detail of this.details) {
      if (detail.field && detail.message) {
        errors[detail.field] = detail.message
      }
    }
    return errors
  }
}

const baseURL = (import.meta.env.VITE_API_BASE_URL ?? '') + '/api/v1'

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 60_000,
  headers: { Accept: 'application/json' },
})

http.interceptors.request.use(async (config) => {
  const token = await getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // The service translates what it owns - report names, column headers - off
  // this header, so switching language in the portal switches those too.
  config.headers['Accept-Language'] = currentLocale()
  return config
})

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorPayload>) => {
    if (!error.response) {
      throw new ApiError(0, 'NETWORK_ERROR',
        'The service could not be reached. Check your connection and try again.')
    }

    const { status, data } = error.response

    // The session has gone, rather than the caller lacking a permission.
    if (status === 401) {
      await login()
      throw new ApiError(401, 'UNAUTHENTICATED', 'Your session has expired. Signing you in again.')
    }

    // A blob response (an export) that failed still carries a JSON error body.
    const payload = await readErrorPayload(data)

    throw new ApiError(
      status,
      payload?.code ?? 'UNKNOWN',
      payload?.message ?? defaultMessage(status),
      payload?.details,
    )
  },
)

async function readErrorPayload(data: unknown): Promise<ApiErrorPayload | null> {
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as ApiErrorPayload
    } catch {
      return null
    }
  }
  return (data as ApiErrorPayload) ?? null
}

function defaultMessage(status: number): string {
  switch (status) {
    case 403: return 'You do not have permission to do that.'
    case 404: return 'That could not be found.'
    case 409: return 'That conflicts with something already saved.'
    case 413: return 'That file is too large.'
    case 422: return 'That cannot be done right now.'
    case 502: return 'An upstream service is unavailable.'
    default: return status >= 500
      ? 'Something went wrong on the server.'
      : 'The request could not be completed.'
  }
}

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return (await http.get<T>(url, config)).data
}

export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return (await http.post<T>(url, body, config)).data
}

export async function put<T>(url: string, body?: unknown): Promise<T> {
  return (await http.put<T>(url, body)).data
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  return (await http.patch<T>(url, body)).data
}

/**
 * Fetches a binary resource with the session's token, for showing in the page
 * through an object URL. An `<img src>` cannot carry the Authorization header,
 * and a token in a URL ends up in logs and history.
 */
export async function getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
  return (await http.get<Blob>(url, { ...config, responseType: 'blob' })).data
}

export async function del(url: string): Promise<void> {
  await http.delete(url)
}

/**
 * Downloads a file and hands it to the browser. Used for every export and
 * template in the portal.
 */
export async function download(
  url: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; params?: Record<string, unknown>; fallbackName: string },
): Promise<void> {
  const response = await http.request<Blob>({
    url,
    method: options.method ?? 'GET',
    data: options.body,
    params: options.params,
    responseType: 'blob',
  })

  const fileName = fileNameFrom(response.headers['content-disposition']) ?? options.fallbackName
  const objectUrl = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  // Revoked on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
}

function fileNameFrom(disposition: unknown): string | null {
  if (typeof disposition !== 'string') {
    return null
  }
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  return match ? decodeURIComponent(match[1]) : null
}
